import type { MenuItem, MenuSection } from "@/config/navigation";
import { ORGANIZATION_ROLE } from "@/constants/organization-role";
import { parseClaimsString } from "@/lib/auth/session-menu-claims";
import type { OrganizationRoleValue } from "@/types/enums/organization-role";

/** Where to send the user when the current path is claim-gated and not allowed. */
export const CLAIM_GATED_ROUTE_FALLBACK_PATH = "/dashboard";

const hasInternalHref = (item: MenuItem): item is MenuItem & { href: string } =>
  typeof item.href === "string" && item.href.startsWith("/");

const pathnameMatchesMenuHref = (pathname: string, href: string): boolean =>
  pathname === href || pathname.startsWith(`${href}/`);

/**
 * Returns whether a menu item should be shown for the given effective menu claims.
 * - If the item has no claims (null), it is visible to everyone.
 * - Otherwise the item is visible if **any** token matches: each `item.claims`
 *   string is split on commas/semicolons/whitespace (same as theme `claims`),
 *   then OR across tokens (e.g. `['marine, surveillance']` matches if the user
 *   has either `marine` or `surveillance`).
 */
function isMenuItemVisibleForClaims(item: MenuItem, menuClaims: string[] | null): boolean {
  if (item.claims === null) return true;
  const effective = menuClaims ?? [];
  const itemTokens = item.claims.flatMap((c) => parseClaimsString(c));
  return itemTokens.some((token) => effective.includes(token));
}

/**
 * Filters menu sections by session-derived claims.
 */
export function filterMenuSectionsByClaims(
  sections: readonly MenuSection[],
  menuClaims: string[] | null,
): readonly MenuSection[] {
  return sections
    .map((section) => ({
      labelKey: section.labelKey,
      items: section.items.filter((item) => isMenuItemVisibleForClaims(item, menuClaims)),
    }))
    .filter((section) => section.items.length > 0);
}

/**
 * True when `pathname` matches a claim-gated sidebar route the effective claims cannot open.
 * Longest matching href wins. Paths with no matching sidebar item are allowed.
 */
export const isClaimGatedPathDisallowed = (
  pathname: string,
  menuClaims: string[] | null,
  sections: readonly MenuSection[],
): boolean => {
  const matched = sections
    .flatMap((section) => section.items)
    .filter(hasInternalHref)
    .filter((item) => pathnameMatchesMenuHref(pathname, item.href))
    .reduce<(MenuItem & { href: string }) | null>((best, item) => {
      if (!best || item.href.length > best.href.length) return item;
      return best;
    }, null);

  if (!matched) return false;
  return !isMenuItemVisibleForClaims(matched, menuClaims);
};

/**
 * Auth dropdown: session menu claims plus optional `adminSettingsHub`.
 *
 * When `activeOrganizationId` is set and the user belongs to more than one organization, the `{organization.name}`
 * section label follows the active organization (sidebar switcher).
 */
export function filterMenuSectionsAuth(
  sections: readonly MenuSection[],
  contextLabel: string,
  menuClaims: string[] | null,
  organizations: { id: string; name: string; isAdmin: boolean }[],
  authProjects?: { id: string; role: OrganizationRoleValue }[],
  activeOrganizationId?: string | null,
): readonly MenuSection[] {
  const firstAdminOrganization = organizations.find((t) => t.isAdmin) ?? null;
  const firstAdminOrganizationId = firstAdminOrganization?.id ?? null;
  const activeOrganization =
    activeOrganizationId != null
      ? (organizations.find((t) => t.id === activeOrganizationId) ?? null)
      : null;

  const organizationName =
    organizations.length > 1 && activeOrganization != null
      ? activeOrganization.name
      : (firstAdminOrganization?.name ?? "");

  const anyProjectAdmin = Boolean(
    authProjects?.some((p) => p.role === ORGANIZATION_ROLE.ADMIN),
  );

  const showAdminSettingsHub = firstAdminOrganizationId != null || anyProjectAdmin;

  return sections
    .map((section) => ({
      labelKey: section.labelKey?.replace("{organization.name}", organizationName) ?? null,
      items: section.items
        .filter((item) => {
          if (item.adminSettingsHub) {
            return showAdminSettingsHub;
          }
          if (item.organizationAdminOnly) {
            return firstAdminOrganizationId != null;
          }
          return isMenuItemVisibleForClaims(item, menuClaims);
        })
        .map((item) => {
          if (item.adminSettingsHub) {
            return { ...item, href: "/admin/settings" };
          }
          return item;
        }),
    }))
    .filter((section) => section.items.length > 0);
}
